"use client";

import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import styles from "../css/deliveryview.module.css";
import {
  fetchCompletedDeliveries,
  fetchDeliveryTracking,
  fetchPendingDeliveries,
} from "../../../../../api/dispensary/Delivery/deliveryApi";

import LiveMap from "../../../../../components/LiveMap";

export default function DeliveryView() {
  const location = useLocation();
  const { orderId } = useParams();

  const [delivery, setDelivery] = useState(location.state?.delivery || null);
  const [loading, setLoading] = useState(!location.state?.delivery);
  const [error, setError] = useState("");

  const [tracking, setTracking] = useState(null);

  useEffect(() => {
    if (delivery) return; // already have from navigation state

    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const [pending, completed] = await Promise.all([
          fetchPendingDeliveries(),
          fetchCompletedDeliveries(),
        ]);
        if (!alive) return;
        const all = [...(Array.isArray(pending) ? pending : []), ...(Array.isArray(completed) ? completed : [])];
        const found = all.find((d) => String(d.orderId) === String(orderId));
        if (found) {
          setDelivery(found);
        } else {
          setError("No delivery details available for this link.");
        }
      } catch (e) {
        if (!alive) return;
        setError("Failed to load delivery details.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (orderId) {
      load();
    } else {
      setLoading(false);
    }

    return () => {
      alive = false;
    };
  }, [delivery, orderId]);

  useEffect(() => {
    if (!orderId) return;
    if (!delivery) return;
    if (delivery.status !== "In Transit" && delivery.status !== "Completed") return;

    let alive = true;
    let timer = null;

    const poll = async () => {
      const data = await fetchDeliveryTracking({ orderId });
      if (!alive) return;
      setTracking(data);
      // Completed deliveries are history-only (no polling)
      if (delivery.status === "In Transit") timer = setTimeout(poll, 12000);
    };

    poll();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [orderId, delivery?.status]);

  const driverPosition = tracking?.driverPosition
    ? [tracking.driverPosition.lng, tracking.driverPosition.lat]
    : null;

  const origin = tracking?.origin
    ? [tracking.origin.lng, tracking.origin.lat]
    : null;

  const destination = tracking?.destination
    ? [tracking.destination.lng, tracking.destination.lat]
    : null;

  const mapCenter = driverPosition || origin || destination || null;
  const historyLineString = tracking?.completedPathLineString || null;
  const plannedLineString = tracking?.route?.lineString || null;
  const lineString = historyLineString || plannedLineString || null;

  const showAutoFocusOnDriver = delivery?.status === "In Transit";

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <div className={styles.mainContent}>
        {loading && <div>Loading delivery details...</div>}
        {!loading && error && <div>{error}</div>}
        {!loading && !error && delivery && (
          <>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.title}>{delivery.scriptNumber || delivery.orderId}</div>
            <span
              className={`${styles.statusBadge} ${
                delivery.status === "In Transit"
                  ? styles.statusInTransit
                  : delivery.status === "Completed"
                  ? styles.statusCompleted
                  : styles.statusPending
              }`}
            >
              {delivery.status}
            </span>
          </div>
          <div className={styles.detailsGrid}>
            <div className={styles.detailRow}>
              <span className={styles.icon}>👤</span>
              <span>{delivery.patient}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.icon}>📍</span>
              <span>{delivery.address}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.icon}>⏰</span>
              <span>{""}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.icon}>🧾</span>
              <span>{delivery.items}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.icon}>💵</span>
              <span>{delivery.amount}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.icon}>💳</span>
              <span>{delivery.paymentType}</span>
            </div>
          </div>
        </div>

        <div className={styles.sectionTitle}>Assigned Driver</div>
        <div className={styles.card}>
          <div className={styles.driverRow}>
            <span className={styles.radio} />
            <span>{delivery.driver || "Unassigned"}</span>
          </div>
          <div className={styles.mapWrapper} title="Live tracking">
            {delivery.status === "In Transit" || delivery.status === "Completed" ? (
              <LiveMap
                center={mapCenter}
                destination={destination}
                driverPosition={driverPosition}
                routeLineString={Array.isArray(lineString) ? lineString : null}
                followDriver={false}
                autoFocusOnDriver={showAutoFocusOnDriver}
                height={380}
                zoom={14}
              />
            ) : (
              <div className={styles.mapPlaceholder}>
                <div className={styles.route} />
                <div className={styles.pin} />
              </div>
            )}
          </div>
        </div>

        {/* Bottom invoice action (Completed only) */}
        {delivery.status === "Completed" && (
          <div className={styles.invoiceFooter}>
            <button
              className={styles.invoiceBtn}
              onClick={(e) => { e.stopPropagation(); /* no-op for now */ }}
            >
              View Invoice
            </button>
          </div>
        )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
